import { openMailto } from "@/utils/mailto";
import Hero from "../components/hero";
import emergencyIcon from "@assets/icons/emergencyIcon.svg";
import bulletPoint from "@assets/icons/bulletPoint.svg";
import heroImage from "@assets/icons/partnerHero.svg";
import categoiesImage from "@assets/images/supplier-categories.png";
import {
  categories,
  procurementData,
  requirements,
  whyPartnerTexts,
} from "../data";
import { RequirementButton } from "../components/button";
import SupplierRegistrationForm from "../components/supplierRegistrationForm";
import mailIcon from "@assets/icons/mail-fill.svg";
import phoneIcon from "@assets/icons/phone-fill.svg";
import Contact from "../components/contact";

const heroData = {
  title: "Strong Partnerships. Safe Infrastructure. Shared Success",
  description:
    "GAZ collaborates with certified suppliers, contractors, and engineering partners who share our commitment to safety, innovation, and operational excellence. Join our approved vendor network and participate in residential, commercial, and industrial LPG/SNG projects across the UAE.",
  image: heroImage,
  buttonText: "Register as a Supplier",
  buttonOnclick: () => openMailto({ subject: "Partnership Inquiry" }),
  footerText:
    "Our licensed technicians and 24/7 emergency response team are always ready to protect your home and business. ",
  footerButtonText: "Report an Emergency Online",
  footerButtonOnclick: () =>
    openMailto({
      subject: "Emergency Report",
      body: "Emergency Type:\nLocation:\nDescription:\n",
    }),
  footerIcon: <img src={emergencyIcon} alt="" className="w-8 h-8" />,
};

export default function Partnerships() {
  return (
    <main className="bg-white">
      <Hero data={heroData} />

      {/* Why Partner with GAZ */}
      <section className="mt-16 md:mt-24 lg:mt-[100px] px-4 md:px-6 lg:px-8">
        <h1 className="text-[28px] md:text-[36px] lg:text-[46px] font-semibold leading-normal text-[#122D63] text-left md:text-center">
          Why Partner with GAZ
        </h1>
        <h2 className="text-[#62636C] text-[16px] md:text-[20px] lg:text-[28px] font-normal mt-4 md:mt-6 lg:mt-[28px] text-left md:text-center leading-normal">
          Discover the benefits of partnering with us
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 lg:gap-8 mt-8 md:mt-12 lg:mt-16 max-w-7xl mx-auto">
          {whyPartnerTexts.map((data, i) => (
            <div
              key={i}
              className="w-full min-h-[139px] border p-4 md:p-6 lg:p-[24px] border-[#62636C33] rounded-[24px] flex flex-col"
            >
              <h1 className="text-[18px] md:text-[20px] lg:text-[24px] text-[#090D2C] font-semibold leading-normal">
                {data.title}
              </h1>
              <h2 className="mt-4 md:mt-6 lg:mt-[24px] text-[16px] md:text-[18px] lg:text-[20px] text-[#0A0E2EBF] font-normal leading-normal flex-1">
                {data.subtitle}
              </h2>
            </div>
          ))}
        </div>
      </section>

      {/* Supplier Partner Categories */}
      <section className="mt-16 md:mt-24 lg:mt-[220px] px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col lg:flex-row justify-center items-center gap-8 md:gap-12 lg:gap-16">
            <div className="flex-1 text-left md:text-center lg:text-left">
              <h1 className="text-[28px] md:text-[36px] lg:text-[46px] font-semibold leading-normal text-[#122D63]">
                Supplier Partner Categories
              </h1>
              <h2 className="text-[#62636C] text-[16px] md:text-[20px] lg:text-[24px] font-normal mt-4 md:mt-6 lg:mt-[28px] leading-normal">
                Supplier Categories We Work With
              </h2>

              <div className="mt-4 md:mt-4 lg:mt-4 space-y-3 md:space-y-4 lg:space-y-6">
                {categories.map((data, i) => (
                  <div
                    className="flex items-center lg:justify-start gap-3 md:gap-4"
                    key={i}
                  >
                    <img src={bulletPoint} alt="" className="flex-shrink-0" />
                    <h3 className="text-[#62636C] text-[16px] md:text-[18px] lg:text-[24px] font-normal leading-normal">
                      {data}
                    </h3>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex-1 flex justify-center">
              <img
                src={categoiesImage}
                alt="Supplier Categories"
                className="max-w-full h-auto"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Supplier Registration Requirements */}
      <section className="mt-16 md:mt-24 lg:mt-[100px] px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-[28px] md:text-[36px] lg:text-[46px] font-semibold leading-normal text-[#122D63] text-left">
            Supplier Registration Requirements
          </h1>
          <h2 className="text-[#62636C] text-[16px] md:text-[20px] lg:text-[24px] font-normal mt-4 md:mt-6 lg:mt-[40px] leading-normal text-left">
            To qualify as an approved partner, suppliers must submit:
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6 lg:gap-8 mt-4 md:mt-4 lg:mt-4">
            {requirements.map((data, i) => (
              <div
                className="flex items-center gap-3 md:gap-4 text-left"
                key={i}
              >
                <img src={bulletPoint} alt="" className="flex-shrink-0" />
                <h3 className="text-[#62636C] text-[16px] md:text-[18px] lg:text-[20px] font-normal leading-normal">
                  {data}
                </h3>
              </div>
            ))}
          </div>
        </div>

        <div className="max-w-7xl mx-auto mt-8 md:mt-12 lg:mt-[53px]">
          <RequirementButton
            title="Download Full Vendor Requirements"
            onClick={() => openMailto({ subject: "Partnership Inquiry" })}
          />
        </div>
      </section>

      {/* Procurement and Tendering Process */}
      <section className="bg-[#F1F1F1] mt-16 md:mt-24 lg:mt-[120px] py-16 md:pt-20 lg:py-[100px] px-4 md:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h1 className="text-[28px] md:text-[36px] lg:text-[46px] font-semibold leading-normal text-[#122D63] text-left">
            Procurement & Tendering Process
          </h1>
          <h2 className="text-[#62636C] text-[16px] md:text-[20px] lg:text-[24px] font-normal mt-4 md:mt-6 lg:mt-[40px] leading-normal text-left">
            Your journey to partnering with GAZ
          </h2>

          <div className="flex flex-col gap-4 md:gap-6 lg:gap-[16px] mt-6 md:mt-8 lg:mt-[32px]">
            {procurementData.map((data, i) => (
              <div
                key={i}
                className="w-full min-h-[111px] rounded-[16px] bg-white p-4 md:p-6 lg:p-[32px]"
              >
                <h1 className="text-[18px] md:text-[20px] lg:text-[24px] font-medium leading-normal">
                  {data.title}
                </h1>
                <h2 className="text-[#62636C] text-[16px] md:text-[18px] lg:text-[20px] font-normal leading-normal">
                  {data.subtitle}
                </h2>
              </div>
            ))}
          </div>
        </div>

        {/* Supplier Registration Form */}
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8">
          <h1 className="text-[28px] md:text-[36px] lg:text-[46px] font-semibold leading-normal text-[#122D63] text-left md:text-center mt-16 md:mt-24 lg:mt-[256px]">
            Supplier Registration Form
          </h1>
          <SupplierRegistrationForm />
        </div>
      </section>

      {/* Contact Section */}
      <div className="mx-auto py-16 md:py-20 lg:py-[100px] px-4 md:px-6 lg:px-8">
        <Contact
          title="For supplier inquiries or procurement matters:"
          subtitle="Call our 24/7 Emergency Line: 800-GAZ (42747) or report a concern via our Online Safety Form."
          cardTitle="Book Consultation"
          contactDetails={[
            { title: "procurement@gazglobal.net", icon: mailIcon },
            { title: "Support (8 AM – 8 PM)", icon: phoneIcon },
          ]}
        />
      </div>
    </main>
  );
}
